import {
  graphQLErrorCode,
  graphQLErrorMessage,
  isConnectivityError,
} from '@/lib/apollo/errors'

const CONNECTION_MSG =
  'No pudimos conectar con el servidor. Verifica tu conexión e intenta de nuevo.'
const SERVICE_MSG =
  'El servicio no está disponible en este momento. Intenta de nuevo más tarde.'
const CREDENTIALS_MSG = 'Correo o contraseña incorrectos.'
const UNEXPECTED_MSG = 'Error inesperado. Intenta de nuevo.'

/**
 * Mensaje para el usuario ante un fallo de **login**.
 *
 * Un login solo falla por: credenciales inválidas, servicio caído o red. Un
 * `INTERNAL_SERVER_ERROR` sí es un error real inesperado y se comunica como tal
 * (honestidad): NO lo disfrazamos de credenciales.
 *
 * - Red / servidor inalcanzable → mensaje de conexión.
 * - `SERVICE_UNAVAILABLE` → servicio caído.
 * - `NOT_FOUND` (email inexistente/inactivo/sin confirmar) o `FORBIDDEN`
 *   (contraseña incorrecta, desde el fix de gRPC PERMISSION_DENIED en el
 *   gateway) → credenciales. No distinguimos cuál para no filtrar qué correos
 *   existen.
 */
export function parseLoginError(err: unknown): string {
  if (isConnectivityError(err)) return CONNECTION_MSG
  const code = graphQLErrorCode(err)
  if (code === 'SERVICE_UNAVAILABLE') return SERVICE_MSG
  if (code === 'NOT_FOUND' || code === 'FORBIDDEN') return CREDENTIALS_MSG
  return UNEXPECTED_MSG
}

/** Mensaje para el usuario ante un fallo al **registrar** un negocio. */
export function parseRegisterError(err: unknown): string {
  if (isConnectivityError(err)) return CONNECTION_MSG
  const msg = graphQLErrorMessage(err)
  if (msg.includes('already registered'))
    return 'Ya existe una cuenta con este correo electrónico.'
  if (msg.includes('already pending'))
    return 'Ya tienes un registro pendiente. Revisa tu correo o WhatsApp para el código.'
  return msg || 'No pudimos completar el registro. Verifica los datos e intenta de nuevo.'
}

/** Mensaje para el usuario ante un fallo al **verificar el código** por email. */
export function parseVerifyError(err: unknown): string {
  if (isConnectivityError(err)) return CONNECTION_MSG
  const msg = graphQLErrorMessage(err)
  if (msg.includes('Invalid verification code'))
    return 'Código incorrecto. Vuelve a intentarlo.'
  if (msg.includes('expired'))
    return 'El código ha expirado. Solicita uno nuevo con "Reenviar".'
  if (msg.includes('No pending registration'))
    return 'No hay registro pendiente. Por favor, vuelve a registrarte.'
  return msg || 'No pudimos verificar el código. Intenta de nuevo.'
}
